import { FC, ReactNode } from 'react';

import { AddDocumentIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { TableRow } from '../../../components/table/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat } from '../../../hooks/index';
import { downloadDocumentStorage } from '../../../storage/modals/index';
import { formatDisplayValue } from '../../../utils/format-display-value.util';
import { fromBackendDateFormat } from '../../../utils/from-backend-date-format.util';
import { toRound } from '../../../utils/get-rounded-number.util';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { percentage } from '../../../utils/percentage.util';
import { ReverseVestingRow } from '../hooks/index';
import {
  ReverseVestingCardRowKey,
  reverseVestingCardRowOrderMap,
  reverseVestingCardRowTranslationKey,
  ReverseVestingColumn,
  reverseVestingColumnOrderMap,
} from './reverse-vesting.types';
import { ReverseVestingCard, ReverseVestingCardShares } from './reverse-vesting-card';

export type ReverseVestingItemProps = { data: ReverseVestingRow; mode?: 'TABLE' };

export const ReverseVestingItem: FC<ReverseVestingItemProps> = ({
  data: {
    documents,
    fullVestingDate,
    id,
    issueDate,
    nextVestingDate,
    reverseVestingShares,
    shareClassName,
    unvestedShares,
    valuationName,
    vestedShares,
  },
  mode,
}) => {
  const open = downloadDocumentStorage(({ open }) => open);
  const { format } = useFormat();

  // Company incorporation has no valuation event of its own — the backend sends null for it.
  const eventName = valuationName ? (
    <Tooltip content={valuationName}>
      <span className="block max-w-[150px] truncate">{valuationName}</span>
    </Tooltip>
  ) : (
    <AppFormattedMessage id="COMPANY_INCORPORATION" />
  );

  const documentsCell = documents.length ? (
    <Tooltip
      content={
        documents.length === 1
          ? getS3FileOriginalName(documents[0])
          : `${documents.length} files attached to event`
      }
    >
      <AddDocumentIcon className="size-6 cursor-pointer p-[2px]" onClick={() => open(documents)} />
    </Tooltip>
  ) : (
    '-'
  );

  // Null once the schedule is fully vested: there is no further milestone to show.
  const nextVestingCell = nextVestingDate
    ? format(fromBackendDateFormat(nextVestingDate), 'dd/MM/yyyy')
    : '-';

  const payload: { id: string } & Record<ReverseVestingColumn, number | ReactNode | string> = {
    [ReverseVestingColumn.VALUATION_EVENT]: eventName,
    [ReverseVestingColumn.ISSUE_DATE]: format(fromBackendDateFormat(issueDate), 'dd/MM/yyyy'),
    [ReverseVestingColumn.SHARE_CLASS]: (
      <Tooltip content={shareClassName}>
        <span className="block max-w-[150px] truncate">{shareClassName}</span>
      </Tooltip>
    ),
    // The schedule's own share count, not the whole issuance: a partial reverse vesting puts only
    // part of the row's shares under repurchase, and that part is what vested + unvested split.
    [ReverseVestingColumn.NO_OF_SHARES]: formatDisplayValue(reverseVestingShares),
    [ReverseVestingColumn.VESTED]: formatDisplayValue(vestedShares),
    [ReverseVestingColumn.UNVESTED]: formatDisplayValue(unvestedShares),
    [ReverseVestingColumn.NEXT_VESTING_DATE]: nextVestingCell,
    [ReverseVestingColumn.FULL_VESTING_DATE]: format(
      fromBackendDateFormat(fullVestingDate),
      'dd/MM/yyyy',
    ),
    [ReverseVestingColumn.DOCUMENTS]: documentsCell,
    id,
  };

  if (mode === 'TABLE')
    return (
      <TableRow
        columnOrder={reverseVestingColumnOrderMap}
        fields={payload}
        labelClassName="text-xs font-[450] text-gray-700"
      />
    );

  // Vested and unvested are the schedule shares still held, so together they are the percentage
  // base — that keeps the two shares of the card adding up to 100% even after a buyback, secondary
  // or conversion took part of the schedule off the holding.
  const sharePercentage = (shares: number) =>
    `(${toRound(percentage(vestedShares + unvestedShares, shares) || 0)}%)`;

  const cardValue: Record<ReverseVestingCardRowKey, ReactNode> = {
    [ReverseVestingCardRowKey.NO_OF_SHARES]: (
      <span>{formatDisplayValue(reverseVestingShares)}</span>
    ),
    [ReverseVestingCardRowKey.VESTED]: (
      <ReverseVestingCardShares
        percentage={sharePercentage(vestedShares)}
        shares={formatDisplayValue(vestedShares)}
      />
    ),
    [ReverseVestingCardRowKey.REMAINING]: (
      <ReverseVestingCardShares
        percentage={sharePercentage(unvestedShares)}
        shares={formatDisplayValue(unvestedShares)}
      />
    ),
    [ReverseVestingCardRowKey.NEXT_VESTING]: <span>{nextVestingCell}</span>,
    [ReverseVestingCardRowKey.FULL_VESTING]: (
      <span>{format(fromBackendDateFormat(fullVestingDate), 'dd/MM/yyyy')}</span>
    ),
    [ReverseVestingCardRowKey.DOCUMENT]: documentsCell,
  };

  return (
    <ReverseVestingCard
      rows={reverseVestingCardRowOrderMap.map((row) => ({
        id: row,
        label: <AppFormattedMessage id={reverseVestingCardRowTranslationKey[row]} />,
        value: cardValue[row],
      }))}
      subtitle={shareClassName}
      title={eventName}
    />
  );
};
